import { createSlice, PayloadAction } from '@reduxjs/toolkit'

interface FilterState {
  searchQuery: string
  activeTags: string[]
  classification: {
    photos: boolean
    screenshots: boolean
    documents: boolean
    textHeavy: boolean
  }
  hasFaces: 'any' | 'yes' | 'no'
  dateRange: {
    start: string | null
    end: string | null
  }
  similarTo: string | null
  // Pagination state - persisted across modal opens/closes
  libraryPage: number
}

const initialState: FilterState = {
  searchQuery: '',
  activeTags: [],
  classification: {
    photos: true,
    screenshots: true,
    documents: true,
    textHeavy: true,
  },
  hasFaces: 'any',
  dateRange: {
    start: null,
    end: null,
  },
  similarTo: null,
  libraryPage: 1,
}

const filterSlice = createSlice({
  name: 'filter',
  initialState,
  reducers: {
    setSearchQuery: (state, action: PayloadAction<string>) => {
      state.searchQuery = action.payload
    },
    addTag: (state, action: PayloadAction<string>) => {
      if (!state.activeTags.includes(action.payload)) {
        state.activeTags.push(action.payload)
      }
    },
    removeTag: (state, action: PayloadAction<string>) => {
      state.activeTags = state.activeTags.filter(t => t !== action.payload)
    },
    setTags: (state, action: PayloadAction<string[]>) => {
      state.activeTags = action.payload
    },
    clearTags: (state) => {
      state.activeTags = []
    },
    setClassification: (state, action: PayloadAction<Partial<FilterState['classification']>>) => {
      state.classification = { ...state.classification, ...action.payload }
    },
    setHasFaces: (state, action: PayloadAction<FilterState['hasFaces']>) => {
      state.hasFaces = action.payload
    },
    setDateRange: (state, action: PayloadAction<FilterState['dateRange']>) => {
      state.dateRange = action.payload
    },
    setSimilarTo: (state, action: PayloadAction<string | null>) => {
      state.similarTo = action.payload
    },
    setLibraryPage: (state, action: PayloadAction<number>) => {
      state.libraryPage = action.payload
    },
    incrementLibraryPage: (state) => {
      state.libraryPage += 1
    },
    resetLibraryPage: (state) => {
      state.libraryPage = 1
    },
    clearFilters: (state) => {
      state.searchQuery = ''
      state.activeTags = []
      state.classification = initialState.classification
      state.hasFaces = 'any'
      state.dateRange = { start: null, end: null }
      state.similarTo = null
      state.libraryPage = 1
    },
  },
})

export const {
  setSearchQuery,
  addTag,
  removeTag,
  setTags,
  clearTags,
  setClassification,
  setHasFaces,
  setDateRange,
  setSimilarTo,
  setLibraryPage,
  incrementLibraryPage,
  resetLibraryPage,
  clearFilters,
} = filterSlice.actions

export default filterSlice.reducer
